import { useAuth0 } from "@auth0/auth0-react";
import { useEffect, useState, useMemo, useCallback } from "react";

interface User {
  id: string;
  auth0Id: string;
  email: string;
  name: string;
  picture?: string;
  // Resolved URL of the user-uploaded avatar (User.avatarKey), preferred over
  // the Auth0 `picture` when present.
  avatarUrl?: string | null;
  newsletter: boolean;
  seeTutorial?: boolean;
  // Verified fallback address for notifications when `email` is unreachable
  // (e.g. Apple private relay). `emailReachable` is server-computed.
  contactEmail?: string | null;
  contactEmailVerifiedAt?: string | null;
  emailReachable?: boolean;
  // PawMatch preferences are stored on the user row as JSON.
  // We keep these broad here and let PawMatch-specific code
  // interpret the structure.
  pawmatchPreferences?: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
}

export const useUserSync = () => {
  const {
    user: auth0User,
    isAuthenticated,
    isLoading,
    getAccessTokenSilently,
  } = useAuth0();
  const [user, setUser] = useState<User | null>(null);
  const [isUserLoading, setIsUserLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const retry = useCallback(() => setRetryCount((c) => c + 1), []);

  // Memoize auth0User to prevent unnecessary re-renders
  const stableAuth0User = useMemo(() => {
    if (!auth0User) return null;
    return {
      sub: auth0User.sub,
      email: auth0User.email,
      name: auth0User.name,
      picture: auth0User.picture,
    };
  }, [auth0User?.sub, auth0User?.email, auth0User?.name, auth0User?.picture]);

  useEffect(() => {
    const syncUser = async () => {
      if (!isAuthenticated || !stableAuth0User || isLoading) return;

      setIsUserLoading(true);
      setError(null);

      try {
        const token = await getAccessTokenSilently({
          authorizationParams: {
            audience: import.meta.env.VITE_AUTH0_AUDIENCE,
          },
        });
        const API_URL = import.meta.env.VITE_REACT_APP_API_URL;
        const response = await fetch(`${API_URL}/api/users`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            auth0Id: stableAuth0User.sub,
            email: stableAuth0User.email,
            name: stableAuth0User.name,
            picture: stableAuth0User.picture,
            newsletter: false, // Default value
            // seeTutorial intentionally omitted here, handled by backend default
          }),
        });

        if (!response.ok) {
          throw new Error(`Failed to sync user: ${response.status}`);
        }

        const result = await response.json();
        if (result.success) {
          setUser(result.user);
        } else {
          throw new Error(result.message || "Failed to sync user");
        }
      } catch (error) {
        console.error("Error syncing user:", error);
        setError(error instanceof Error ? error.message : "Unknown error");
      } finally {
        setIsUserLoading(false);
      }
    };

    syncUser();
  }, [
    getAccessTokenSilently,
    isAuthenticated,
    stableAuth0User,
    isLoading,
    retryCount,
  ]);

  return { user, isUserLoading, error, retry };
};
